import { money, fmtDate, fmtTime, fmtDateTime } from "@/lib/format";
import { PrintBrand, ThermalBrand } from "@/components/Brand";

// Bus sign-out account slip: ticket revenue split into Platform Operator, Tracking and the Station Master balance (insurance kept in a separate account)
export default function SettlementView({ s, format = "a4" }) {
  const t = s.trip;
  const total = s.lines.reduce((a, l) => a + l.amount, 0);
  if (format === "thermal") {
    return (
      <div className="thermal">
        <ThermalBrand title="BUS SIGN-OUT SLIP" />
        <div className="center">{s.number}</div>
        <hr />
        <div className="center">{t.origin_city} &gt; {t.destination_city}</div>
        <div className="center">{fmtDate(t.departure)} {fmtTime(t.departure)} · Bus {t.bus_number}</div>
        <div className="center">{t.tracker_active ? `Tracker ID: ${t.tracker_id}` : "No tracker installed"}</div>
        <div className="row"><span>Tickets</span><span>{s.tickets}</span></div>
        <div className="row"><span>Fare revenue</span><span>{money(s.gross_fare)}</span></div>
        <hr />
        {s.lines.map((l) => (
          <div key={l.role}><div className="row"><span>{l.label}</span><span>{money(l.amount)}</span></div><div>{l.basis}{l.momo ? ` · ${l.momo}` : ""}</div></div>
        ))}
        <hr />
        <div className="row big"><span>TOTAL</span><span>{money(total)}</span></div>
        <div className="row"><span>Insurance (separate)</span><span>{money(s.insurance_total)}</span></div>
        <div className="center">{s.status === "disbursed" ? `DISBURSED ${fmtDateTime(s.disbursed_at)}` : "PENDING DISBURSEMENT"}</div>
      </div>
    );
  }
  return (
    <div className="a4 text-sm" data-testid="settlement-view">
      <PrintBrand title="Bus Sign-out Account Slip" />
      <div className="mt-3 flex items-start justify-between border-b border-slate-200 pb-3">
        <div>
          <div className="font-semibold">{t.origin} ({t.origin_city}) → {t.destination} ({t.destination_city})</div>
          <div className="text-xs text-slate-500">Slip {s.number} · signed out by {s.signed_out_by}, {fmtDateTime(s.signed_out_at)}</div>
        </div>
        <div className="text-right text-xs">
          <div><b>Departure:</b> {fmtDate(t.departure)} {fmtTime(t.departure)}</div>
          <div><b>Bus:</b> {t.bus_number} ({t.bus_type})</div>
          <div><b>Driver:</b> {t.driver_name}</div>
          <div data-testid="settlement-tracker"><b>Tracker:</b> {t.tracker_active ? t.tracker_id : "No tracker installed"}</div>
          <div className={`mt-1 font-bold uppercase ${s.status === "disbursed" ? "text-emerald-600" : "text-amber-600"}`} data-testid="settlement-status">{s.status === "disbursed" ? "Disbursed" : "Pending disbursement"}</div>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-3">
        <div className="rounded-xl bg-blue-50 p-3"><div className="label-xs">Tickets sold</div><div className="font-display text-xl font-extrabold">{s.tickets} / {t.capacity}</div></div>
        <div className="rounded-xl bg-blue-50 p-3"><div className="label-xs">Fare revenue</div><div className="font-display text-xl font-extrabold" data-testid="settlement-gross">{money(s.gross_fare)}</div></div>
        <div className="rounded-xl bg-emerald-50 p-3"><div className="label-xs">Insurance (not shared)</div><div className="font-display text-xl font-extrabold">{money(s.insurance_total)}</div></div>
      </div>
      <table className="mt-4 w-full text-left text-xs">
        <thead><tr className="border-b border-slate-300 bg-slate-50"><th className="p-2">Beneficiary</th><th>Name</th><th>MoMo</th><th>Basis</th><th>Status / ref</th><th className="p-2 text-right">Amount</th></tr></thead>
        <tbody>
          {s.lines.map((l) => (
            <tr key={l.role} className="border-b border-slate-100" data-testid={`settlement-line-${l.role}`}>
              <td className="p-2 font-bold">{l.label}</td><td>{l.name || "-"}</td><td>{l.momo || "-"}</td><td>{l.basis}</td>
              <td>{l.status ? `${l.status}${l.reference ? ` · ${l.reference}` : ""}` : "-"}</td>
              <td className="p-2 text-right font-bold">{money(l.amount)}</td>
            </tr>
          ))}
          <tr><td colSpan={5} className="p-2 text-right font-bold">Total distributed</td><td className="p-2 text-right font-display text-base font-extrabold">{money(total)}</td></tr>
        </tbody>
      </table>
      {s.status === "disbursed" && <p className="mt-2 text-xs text-slate-500">Paid out from the central MoMo account by {s.disbursed_by} on {fmtDateTime(s.disbursed_at)}.</p>}
      <div className="mt-10 grid grid-cols-3 gap-6 text-xs">
        <div className="border-t border-slate-400 pt-1">Station master</div>
        <div className="border-t border-slate-400 pt-1">Driver</div>
        <div className="border-t border-slate-400 pt-1">Accounts</div>
      </div>
    </div>
  );
}
